import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Timeline as TimelineIcon,
  Visibility,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import { metricsService } from '../services/metricsService';
import { pagesService, type PageData } from '../services/pagesService';
import { useSiteStore } from '../store/site.store';

export const PagesListPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedSite = useSiteStore(state => state.selectedSite);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sourceFilter, setSourceFilter] = useState<string>('ALL');

  const selectedSiteId = selectedSite?.id;

  // Fetch all pages from pages table instead of just "top pages"
  const { data: pagesData, isLoading, error } = useQuery({
    queryKey: ['allPages', selectedSiteId, searchQuery, page, rowsPerPage, sourceFilter],
    queryFn: async () => {
      if (!selectedSiteId) return null;
      
      return await pagesService.getAllPages({
        siteId: selectedSiteId,
        isActive: true,
        search: searchQuery || undefined,
        source: sourceFilter !== 'ALL' ? sourceFilter as 'GSC' | 'BING' | 'GA4' : undefined,
        page: page,
        size: rowsPerPage,
        sortBy: 'lastSeenAt',
        sortDirection: 'DESC'
      });
    },
    enabled: !!selectedSiteId,
  });

  const pages = pagesData?.content || [];
  const totalPages = pagesData?.totalElements || 0;

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleViewChanges = (pageUrl: string) => {
    // Use new by-url endpoint for changes lookup
    // Allows customers to query by page URL instead of page ID
    navigate(`/pages/changes/by-url?pageUrl=${encodeURIComponent(pageUrl)}`);
  };

  const handleViewOCI = (pageUrl: string) => {
    // Use new by-url endpoint for OCI lookup
    // Allows customers to query by page URL instead of page ID
    navigate(`/pages/oci/by-url?pageUrl=${encodeURIComponent(pageUrl)}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load pages. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" flexDirection="column" gap={3}>        {/* Page Purpose Description - TODO: Refine text later */}
        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="body2">
            <strong>Pages List:</strong> View performance metrics from Google Search Console (GSC) for all tracked pages. 
            Shows historical data including clicks, impressions, CTR, and average position over the last 28 days. 
            Use the <PsychologyIcon fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.5 }} /> button to view OCI (Observed Competitiveness Index) scores,
            or the <TimelineIcon fontSize="small" sx={{ verticalAlign: 'middle', mx: 0.5 }} /> button to analyze change history.
          </Typography>
        </Alert>

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h3" component="h1" gutterBottom>
              All Pages
            </Typography>
            <Typography variant="body1" color="text.secondary">
              View and analyze SEO performance for all tracked pages from all data sources
            </Typography>
          </Box>
        </Box>

        {/* Source Filter */}
        <Paper sx={{ p: 2 }}>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              fullWidth
              placeholder="Search pages by URL..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0); // Reset to first page on search
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Box display="flex" gap={1}>
              <Chip
                label="All Sources"
                color={sourceFilter === 'ALL' ? 'primary' : 'default'}
                onClick={() => setSourceFilter('ALL')}
                variant={sourceFilter === 'ALL' ? 'filled' : 'outlined'}
              />
              <Chip
                label="GSC"
                color={sourceFilter === 'GSC' ? 'primary' : 'default'}
                onClick={() => setSourceFilter('GSC')}
                variant={sourceFilter === 'GSC' ? 'filled' : 'outlined'}
              />
              <Chip
                label="Bing"
                color={sourceFilter === 'BING' ? 'secondary' : 'default'}
                onClick={() => setSourceFilter('BING')}
                variant={sourceFilter === 'BING' ? 'filled' : 'outlined'}
              />
              <Chip
                label="GA4"
                color={sourceFilter === 'GA4' ? 'success' : 'default'}
                onClick={() => setSourceFilter('GA4')}
                variant={sourceFilter === 'GA4' ? 'filled' : 'outlined'}
              />
            </Box>
          </Box>
        </Paper>

        {/* Results Summary */}
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {totalPages.toLocaleString()} pages found
          </Typography>
          {searchQuery && (
            <Chip
              label={`Filtered by: "${searchQuery}"`}
              onDelete={() => setSearchQuery('')}
              size="small"
            />
          )}
          {sourceFilter !== 'ALL' && (
            <Chip
              label={`Source: ${sourceFilter}`}
              onDelete={() => setSourceFilter('ALL')}
              size="small"
              color="primary"
            />
          )}
        </Box>

        {/* Pages Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Data Sources</TableCell>
                  <TableCell>Last Seen</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pages.length > 0 ? (
                  pages.map((pageData: PageData) => (
                    <TableRow key={pageData.id} hover>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={pageData.url}
                      >
                        {pageData.url}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {pageData.discoveredFrom.includes('GSC') && (
                            <Tooltip title={`GSC: Last seen ${formatDate(pageData.lastSeenInGsc)}`}>
                              <Chip label="GSC" size="small" color="primary" />
                            </Tooltip>
                          )}
                          {pageData.discoveredFrom.includes('BING') && (
                            <Tooltip title={`Bing: Last seen ${formatDate(pageData.lastSeenInBing)}`}>
                              <Chip label="Bing" size="small" color="secondary" />
                            </Tooltip>
                          )}
                          {pageData.discoveredFrom.includes('GA4') && (
                            <Tooltip title={`GA4: Last seen ${formatDate(pageData.lastSeenInGa4)}`}>
                              <Chip label="GA4" size="small" color="success" />
                            </Tooltip>
                          )}
                          {pageData.discoveredFrom.length === 0 && (
                            <Chip label="Unknown" size="small" variant="outlined" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(pageData.lastSeenAt)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          First seen: {formatDate(pageData.firstSeenAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="View Anomaly Detection">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => navigate(`/analytics/anomaly?pageId=${pageData.id}`)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchQuery || sourceFilter !== 'ALL'
                          ? 'No pages found matching your filters'
                          : 'No pages available'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {pages.length > 0 && (
            <TablePagination
              component="div"
              count={totalPages}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default PagesListPage;
